(function () {
  if (customElements.get('announcement-bar-component')) return;

  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const pad = (value) => String(value).padStart(2, '0');

  let lucidePromise;
  const loadLucide = () => {
    if (window.lucide) return Promise.resolve(window.lucide);
    if (!lucidePromise) {
      lucidePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lucide@latest';
        script.async = true;
        script.onload = () => resolve(window.lucide);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return lucidePromise;
  };

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.storageKey = 'announcement-closed-' + this.dataset.sectionId;

      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.initClose();
      if (this.mode === 'marquee') this.initMarquee();
      if (this.mode === 'slider') this.initSlider();
      this.initCountdowns();
      this.initLucideIcons();

      if (isDesignMode()) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      clearInterval(this.countdownTimer);
      this.stopAutoplay();
      cancelAnimationFrame(this.resizeFrame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* Lucide icons */

    initLucideIcons() {
      if (!this.querySelector('[data-lucide]')) return;

      loadLucide()
        .then((lucide) => {
          if (!lucide || !this.isConnected) return;
          lucide.createIcons({ root: this, attrs: { 'aria-hidden': 'true' } });
          if (this.marquee && this.originals && this.originals.length) this.buildMarquee();
        })
        .catch(() => {});
    }

    /* Close button */

    initClose() {
      const button = this.querySelector('[data-ab-close]');
      if (!button) return;

      button.addEventListener('click', () => {
        this.style.height = this.offsetHeight + 'px';
        this.offsetHeight;
        this.classList.add('is-closing');
        this.style.height = '0px';

        if (!isDesignMode()) {
          try {
            sessionStorage.setItem(this.storageKey, '1');
          } catch (e) {}
        }

        setTimeout(() => {
          this.hidden = true;
          if (isDesignMode()) {
            this.hidden = false;
            this.classList.remove('is-closing');
            this.style.height = '';
          }
        }, 350);
      });
    }

    /* Scrolling ticker */

    initMarquee() {
      this.marquee = this.querySelector('[data-marquee]');
      if (!this.marquee) return;

      this.track = this.marquee.querySelector('.ab-marquee__track');
      this.group = this.track.querySelector('.ab-marquee__group');
      this.originals = Array.from(this.group.children);
      if (!this.originals.length) return;

      this.lastWidth = 0;
      this.buildMarquee();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (Math.abs(this.marquee.offsetWidth - this.lastWidth) < 2) return;
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => this.buildMarquee());
        });
        this.resizeObserver.observe(this.marquee);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.buildMarquee());
      }
    }

    buildMarquee() {
      const width = this.marquee.offsetWidth;
      this.lastWidth = width;
      this.track.querySelectorAll('[data-ab-clone]').forEach((node) => node.remove());
      if (!width) return;

      // The group must be at least as wide as the viewport for the loop to stay seamless.
      let guard = 0;
      while (this.group.offsetWidth < width && guard < 30) {
        this.originals.forEach((node) => this.group.appendChild(this.makeClone(node)));
        guard++;
      }

      this.track.appendChild(this.makeClone(this.group));

      const speed = parseFloat(this.dataset.speed) || 50;
      this.track.style.setProperty('--ab-duration', (this.group.offsetWidth / speed).toFixed(2) + 's');
      this.marquee.classList.add('is-ready');
    }

    makeClone(node) {
      const clone = node.cloneNode(true);
      clone.setAttribute('data-ab-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      [clone, ...clone.querySelectorAll('*')].forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
        el.removeAttribute('id');
        if (el.matches('a, button, input, select, textarea')) el.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    /* Rotating slider */

    initSlider() {
      this.slider = this.querySelector('[data-slider]');
      if (!this.slider) return;

      this.slides = Array.from(this.slider.querySelectorAll('.ab-slide'));
      this.index = 0;
      if (this.slides.length < 2) return;

      this.delay = (parseFloat(this.dataset.autoplay) || 4) * 1000;

      const prev = this.slider.querySelector('[data-ab-prev]');
      const next = this.slider.querySelector('[data-ab-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.slider.addEventListener('mouseenter', () => this.pause());
      this.slider.addEventListener('mouseleave', () => this.resume());
      this.slider.addEventListener('focusin', () => this.pause());
      this.slider.addEventListener('focusout', () => this.resume());

      let touchStartX = null;
      this.slider.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.touches[0].clientX;
          this.pause();
        },
        { passive: true }
      );
      this.slider.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) > 40) this.step(delta < 0 ? 1 : -1);
        touchStartX = null;
        this.resume();
      });

      this.startAutoplay();
    }

    step(direction) {
      this.goTo(this.index + direction);
      this.startAutoplay();
    }

    goTo(target) {
      const total = this.slides.length;
      const nextIndex = (target + total) % total;
      if (nextIndex === this.index) return;

      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      setTimeout(() => current.classList.remove('is-leaving'), 600);

      incoming.classList.remove('is-leaving');
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');

      this.index = nextIndex;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.slides || this.slides.length < 2 || this.editorLocked) return;
      this.autoplayTimer = setInterval(() => {
        if (!this.paused) this.goTo(this.index + 1);
      }, this.delay);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
    }

    pause() {
      this.paused = true;
    }

    resume() {
      this.paused = false;
    }

    /* Countdown */

    initCountdowns() {
      if (!this.querySelector('[data-countdown]')) return;

      const tick = () => {
        const now = Date.now();
        const timers = this.querySelectorAll('[data-countdown]');
        if (!timers.length) {
          clearInterval(this.countdownTimer);
          return;
        }

        timers.forEach((timer) => {
          const remaining = parseInt(timer.dataset.end, 10) - now;

          if (!(remaining > 0)) {
            timer.removeAttribute('data-countdown');
            if (timer.dataset.expiredText) {
              timer.textContent = timer.dataset.expiredText;
              timer.classList.add('is-expired');
            } else {
              (timer.closest('.ab-item') || timer).hidden = true;
            }
            return;
          }

          const values = {
            d: Math.floor(remaining / 86400000),
            h: Math.floor((remaining % 86400000) / 3600000),
            m: Math.floor((remaining % 3600000) / 60000),
            s: Math.floor((remaining % 60000) / 1000),
          };

          timer.querySelectorAll('[data-unit]').forEach((unit) => {
            const value = pad(values[unit.dataset.unit]);
            if (unit.textContent !== value) unit.textContent = value;
          });
        });
      };

      tick();
      this.countdownTimer = setInterval(tick, 1000);
    }

    /* Theme editor */

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;

      if (this.marquee) this.marquee.classList.add('is-paused');

      if (this.slides && this.slides.length > 1) {
        const index = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
        this.editorLocked = true;
        this.stopAutoplay();
        if (index > -1) this.goTo(index);
      }
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;

      if (this.marquee) this.marquee.classList.remove('is-paused');

      if (this.slides) {
        this.editorLocked = false;
        this.startAutoplay();
      }
    }
  }

  customElements.define('announcement-bar-component', AnnouncementBar);
})();
